import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LocationType, ProviderTokenTypes, SAVED_LOCATION } from '@guryou/core';
import { Block, CheckBox, GoogleMap, GoogleMapsAutocomplete, HiddenField, PaperBlock, SelectOption } from '@guryou/html-components';
import { sizes } from '@guryou/html-theme';
import { getProviderAddresses, stateIsLoaded } from '@guryou/redux';
import { parseGoogleMapLocation, parseGoogleMapPlace } from '@guryou/utils';

import { dealTokens } from './designTokens';
import LocationUrl from './LocationUrl';
import LocationUrlWithZoom from './LocationUrlWithZoom';
import { SystemFieldControl, SystemSelect } from './SystemFormControls';

// const hasAddress = values => {
//   return values?.address && values?.latitude && values?.longitude;
// };

const defineSelectedViewValue = (location, externalProvider) => {
  if (location?.address) {
    return LocationType.mapLocation;
  } else if (!externalProvider && (location?.note || location?.url)) {
    return LocationType.plainNote;
  } else if (externalProvider && ProviderTokenTypes.google === externalProvider) {
    return LocationType.google;
  } else if (externalProvider && ProviderTokenTypes.zoom === externalProvider) {
    return LocationType.zoom;
  } else {
    return LocationType.providerLocation;
  }
};

const LocationPicker = ({
  showCheckBox,
  checkBoxLabel,
  location,
  externalProvider,
  xs = 12,
  disabled = false,
  noBorder = false,
  fullWidth = false,
  includeZoom = false,
  fromCalendar = false,
  systemDesign = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mapsAutocomplete = useRef();
  const [locationObject, setLocationObject] = useState(location || {});
  const [selectedView, setSelectedView] = useState(defineSelectedViewValue(location, externalProvider));
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const providerTypeTokens = useSelector(state => state.provider.providerTypeTokens);
  const addressesState = useSelector(state => state.addresses.all);

  useEffect(() => {
    if (!stateIsLoaded(addressesState)) {
      dispatch(getProviderAddresses(true));
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const homeAddresses = stateIsLoaded(addressesState) ? (addressesState.data || []).filter(a => a.type === 'business') : [];

  // const providerDetails = useSelector(state => state.provider.generalInfo);

  const handleAutocompleteChange = e => {
    if (!e?.target?.value) {
      setLocationObject({});
    }
  };

  const handleAutocompleteOnChange = e => {
    if (!e?.target?.value) {
      setLocationObject({});
    }
    const val = e?.target?.value;
    setLocationObject(prev => ({ ...prev, address: val }));
  };

  const locationTypeData = useMemo(() => {
    const arr = [
      { label: t('GENERAL.providerLocation'), value: LocationType.providerLocation },
      { label: t('GENERAL.savedLocation'), value: SAVED_LOCATION },
      { label: t('GENERAL.chooseLocationFromMap'), value: LocationType.mapLocation },
      { label: t('GENERAL.onlineLocation'), value: LocationType.plainNote },
    ];
    if (providerTypeTokens?.data?.length) {
      const externalProviderData = providerTypeTokens?.data?.map(x => ({
        label: `${x?.type.charAt(0).toUpperCase() + x?.type.slice(1)} ${x?.type === ProviderTokenTypes.google ? ' Meet' : ''}`,
        value: LocationType[x?.type],
      }));
      return [...arr, ...externalProviderData];
    }
    if (!providerTypeTokens?.data?.length && externalProvider) {
      const externalProviderData = [
        {
          label: `${externalProvider?.charAt(0).toUpperCase() + externalProvider?.slice(1)} ${
            externalProvider === ProviderTokenTypes.google ? ' Meet' : ''
          }`,
          value: LocationType[externalProvider],
        },
      ];
      return [...arr, ...externalProviderData];
    }
    return arr;
  }, [t, providerTypeTokens.data, externalProvider]);

  const Root = systemDesign ? 'div' : PaperBlock;
  const locationTypeSelectProps = {
    disabled: disabled || (!!externalProvider && fromCalendar),
    fullWidth,
    value: selectedView,
    label: t('GENERAL.location'),
    name: 'locationType',
    none: false,
    options: locationTypeData,
    onChange: val => {
      setSelectedView(val);
      if (val !== SAVED_LOCATION) {
        setSelectedAddressId('');
        setLocationObject({});
      }
    },
  };

  return (
    <Root
      {...(systemDesign
        ? { style: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'center', width: '100%' } }
        : { xs, display: 'flex', flexWrap: 'wrap', padding: 1, border: noBorder && 'none', boxShadow: noBorder && 'none', alignItems: 'start' })}
    >
      {systemDesign ? (
        <div style={{ width: '100%' }}>
          <SystemSelect {...locationTypeSelectProps} />
        </div>
      ) : (
        <SelectOption {...locationTypeSelectProps} disabled={disabled || (!!externalProvider && fromCalendar)} xs={fullWidth && 12} />
      )}
      {selectedView === LocationType.google && <HiddenField name="externalProvider" value={ProviderTokenTypes.google} />}
      {selectedView === LocationType.zoom && <HiddenField name="externalProvider" value={ProviderTokenTypes.zoom} />}

      {selectedView === LocationType.plainNote &&
        (includeZoom ? (
          <LocationUrlWithZoom locationObject={locationObject} fullWidth={fullWidth} disabled={disabled} />
        ) : (
          <LocationUrl locationObject={locationObject} fullWidth={fullWidth} disabled={disabled} />
        ))}
      {selectedView === LocationType.mapLocation && (
        <>
          <HiddenField name="latitude" value={locationObject.latitude || ''} />
          <HiddenField name="longitude" value={locationObject.longitude || ''} />
          <HiddenField name="address" value={locationObject.address || ''} />
          {systemDesign ? (
            <div style={{ width: '100%', marginTop: dealTokens.space[4] }}>
              <SystemFieldControl label={t('GENERAL.location')}>
                <GoogleMapsAutocomplete
                  fullWidth
                  disabled={disabled}
                  inputRef={mapsAutocomplete}
                  value={locationObject?.address || ''}
                  onBlur={handleAutocompleteChange}
                  onChange={handleAutocompleteOnChange}
                  placeholder={t('GENERAL.enterLocation')}
                  margin="none"
                  InputProps={{ className: 'studio-input' }}
                  required
                />
              </SystemFieldControl>
            </div>
          ) : (
            <GoogleMapsAutocomplete
              fullWidth={fullWidth}
              xs={fullWidth && 12}
              disabled={disabled}
              label={t('GENERAL.location')}
              inputRef={mapsAutocomplete}
              value={locationObject?.address || ''}
              onBlur={handleAutocompleteChange}
              onChange={handleAutocompleteOnChange}
              placeholder={t('GENERAL.enterLocation')}
              required
            />
          )}
          {systemDesign ? (
            <div
              style={{
                width: '100%',
                marginTop: dealTokens.space[5],
                overflow: 'hidden',
                border: `1px solid ${dealTokens.color.border}`,
                borderRadius: dealTokens.radius.lg,
                background: dealTokens.color.canvas,
                boxShadow: '0 2px 8px rgba(37, 37, 37, 0.06)',
              }}
            >
              <GoogleMap
                style={{ height: 340, minHeight: `${sizes.small}px`, width: '100%', maxWidth: 'none' }}
                defaultCenter={{ lat: locationObject.latitude ?? 41.9028, lng: locationObject.longitude ?? 12.4964 }}
                autocompleteRef={mapsAutocomplete}
                onMapClick={location => setLocationObject(parseGoogleMapLocation(location))}
                onPlaceSelected={place => setLocationObject(parseGoogleMapPlace(place))}
              />
            </div>
          ) : (
            <Block margin={4}>
              <GoogleMap
                style={{ height: '25vw', minHeight: `${sizes.small}px`, width: `100%`, maxWidth: `${sizes.small}px` }}
                defaultCenter={{ lat: locationObject.latitude ?? 41.9028, lng: locationObject.longitude ?? 12.4964 }}
                autocompleteRef={mapsAutocomplete}
                onMapClick={location => setLocationObject(parseGoogleMapLocation(location))}
                onPlaceSelected={place => setLocationObject(parseGoogleMapPlace(place))}
              />
            </Block>
          )}
        </>
      )}

      {selectedView === SAVED_LOCATION && (
        <>
          <HiddenField name="latitude" value={locationObject.latitude || ''} />
          <HiddenField name="longitude" value={locationObject.longitude || ''} />
          <HiddenField name="address" value={locationObject.address || ''} />
          {systemDesign ? (
            <div style={{ width: '100%', marginTop: dealTokens.space[4] }}>
              <SystemSelect
                fullWidth
                disabled={disabled}
                value={selectedAddressId}
                label={t('GENERAL.chooseSavedAddress')}
                name="savedLocationId"
                none={false}
                options={homeAddresses.map(a => ({
                  label: [a.label || a.recipientName, a.line1, a.city].filter(Boolean).join(' — ') || a.line1,
                  value: a.id,
                }))}
                onChange={val => {
                  const addr = homeAddresses.find(a => a.id === val);
                  if (!addr) return;
                  setSelectedAddressId(val);
                  setLocationObject({
                    latitude: addr.latitude || null,
                    longitude: addr.longitude || null,
                    address: [addr.line1, addr.line2, addr.city, addr.country].filter(Boolean).join(', '),
                  });
                }}
              />
            </div>
          ) : (
            <SelectOption
              fullWidth={fullWidth}
              xs={fullWidth && 12}
              disabled={disabled}
              value={selectedAddressId}
              label={t('GENERAL.chooseSavedAddress')}
              none={false}
              options={homeAddresses.map(a => ({
                label: [a.label || a.recipientName, a.line1, a.city].filter(Boolean).join(' — ') || a.line1,
                value: a.id,
              }))}
              onChange={val => {
                const addr = homeAddresses.find(a => a.id === val);
                if (!addr) return;
                setSelectedAddressId(val);
                setLocationObject({
                  latitude: addr.latitude || null,
                  longitude: addr.longitude || null,
                  address: [addr.line1, addr.line2, addr.city, addr.country].filter(Boolean).join(', '),
                });
              }}
            />
          )}
        </>
      )}

      {/* {selectedView === LocationType.providerLocation && hasAddress(providerDetails?.data) && (
        <>
          <HiddenField name="latitude" value={providerDetails.data?.latitude || ''} />
          <HiddenField name="longitude" value={providerDetails.data?.longitude || ''} />
          <HiddenField name="address" value={providerDetails.data?.address || ''} />
        </>
      )} */}
      {showCheckBox && (
        <Block whiteSpace="normal">
          <CheckBox fullWidth={true} label={checkBoxLabel} name="propagateLocation" value={true} checked={false} />
        </Block>
      )}
    </Root>
  );
};

export default LocationPicker;
